import React from 'react'

import { Dashboard, PageLayout, SideSummary } from '@components'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  BackButton,
  Dropdown,
  HeadContent,
  SaveShortcutListener,
  ToolbarButton,
  useMediaQueryContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { SingleSeriesSkeleton } from './SingleSeriesSkeleton'
import { useSingleSeriesContext } from './hooks/useSingleSeriesContext'
import { ArtworkFields } from './modules/ArtworkFields'
import { DetailsFields } from './modules/DetailsFields'
import { ThumbnailFields } from './modules/ThumbnailFields'
import { VideosFields } from './modules/VideosFields'
import { SingleSeriesProvider } from './providers/SingleSeriesProvider'

const SingleSeriesComponent = () => {
  const media = useMediaQueryContext()
  const { singleSeries, form, singleSeriesLink, deleteSingleSeries, isLoading } =
    useSingleSeriesContext()

  if (!singleSeries) {
    return <SingleSeriesSkeleton />
  }

  return (
    <>
      <HeadContent title={singleSeries.title} />

      <PageLayout
        leftContent={
          <>
            <DetailsFields />
            <ThumbnailFields />
            <ArtworkFields />
            <VideosFields />
          </>
        }
        rightContent={
          <SideSummary>
            <SideSummary.Item title="Stats">Coming soon</SideSummary.Item>
          </SideSummary>
        }
      >
        <Dashboard.ItemViewHeader
          name={singleSeries.title}
          backButton={
            <BackButton
              label="Video series"
              to={{
                pathname: routing.memberships.series.self,
                search: location.search,
              }}
            />
          }
          toolbar={
            <Dropdown
              content={<Dropdown.Item label="Delete" onClick={deleteSingleSeries} />}
              position="bottom-end"
            >
              <ToolbarButton
                label="Settings"
                icon={DotsHorizontalIcon}
                size={media.tablet ? 'medium' : 'small'}
              />
            </Dropdown>
          }
          link={
            singleSeriesLink
              ? {
                  label: singleSeriesLink,
                  url: singleSeriesLink,
                  referrerPolicy: 'unsafe-url', // Memberships detects a creator auth context if /admin/dashboard is present in the Referrer header. The default browser Referrer policy is 'strict-origin-when-cross-origin', which removes the path for cross-origin requests (the Core dashboard is always on the internal Fourthwall domain). The `unsafe-url` policy preserves the full path
                }
              : undefined
          }
        />
      </PageLayout>

      <SaveShortcutListener disabled={!form.dirty} onSave={form.submitForm} />
      <ConfirmBar
        isOpen={form.dirty}
        onConfirm={form.submitForm}
        onCancel={form.resetForm}
        isLoading={isLoading}
      />
      <LeavingGuard when={form.dirty} isValid={form.isValid} onSave={form.submitForm} />
    </>
  )
}

export const SingleSeries = () => (
  <SingleSeriesProvider>
    <SingleSeriesComponent />
  </SingleSeriesProvider>
)
